import type { UnifiedContactResponse } from "../../../types/crm";
import { MultiSelectPopover } from "../../../components/MultiSelectPopover";
import { US_STATES } from "../../../constants/usStates";
import { DIVISIONS } from "../../../constants/divisions";
import {
  PIPELINE_STAGE_LABELS,
  pipelineStageLabel,
  pipelineStageValue,
} from "../../../constants/pipelineStages";
import type { PipelineStageValue } from "../../../constants/pipelineStages";
import "./ContactsToolbar.css";

interface ContactsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  divisions: string[];
  onDivisionsChange: (value: string[]) => void;
  states: string[];
  onStatesChange: (value: string[]) => void;
  projectedDivisions: string[];
  onProjectedDivisionsChange: (value: string[]) => void;
  /** API `PipelineStage` values, not display labels. */
  pipelineStages: string[];
  onPipelineStagesChange: (value: string[]) => void;
  contactType: string;
  selectedContacts: UnifiedContactResponse[];
  onBulkSend: () => void;
  onAddContact: () => void;
  onExportEmails: () => void;
  exporting: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  Coach: "Coach",
  ProspectProfile: "Player",
  Parent: "Parent",
};

export const ContactsToolbar = ({
  search,
  onSearchChange,
  divisions,
  onDivisionsChange,
  states,
  onStatesChange,
  projectedDivisions,
  onProjectedDivisionsChange,
  pipelineStages,
  onPipelineStagesChange,
  contactType,
  selectedContacts,
  onBulkSend,
  onAddContact,
  onExportEmails,
  exporting,
}: ContactsToolbarProps) => {
  const isCoach = contactType === "Coach";
  const isPlayer = contactType === "ProspectProfile";
  const selectedCount = selectedContacts.length;
  const typeLabel = TYPE_LABELS[contactType] ?? contactType;

  return (
    <div className="contacts-toolbar">
      <div className="contacts-toolbar__filters">
        <input
          type="text"
          className="contacts-toolbar__search"
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />

        {isCoach && (
          <>
            <div className="contacts-toolbar__filter-division">
              <MultiSelectPopover
                options={DIVISIONS}
                value={divisions}
                onChange={onDivisionsChange}
                placeholder="All Divisions"
              />
            </div>
            <div className="contacts-toolbar__filter-state">
              <MultiSelectPopover
                options={US_STATES}
                value={states}
                onChange={onStatesChange}
                placeholder="State"
                searchable
              />
            </div>
          </>
        )}

        {isPlayer && (
          <>
            <div className="contacts-toolbar__filter-division">
              <MultiSelectPopover
                options={DIVISIONS}
                value={projectedDivisions}
                onChange={onProjectedDivisionsChange}
                placeholder="Projected Division"
              />
            </div>
            <div className="contacts-toolbar__filter-stage">
              <MultiSelectPopover
                options={[...PIPELINE_STAGE_LABELS]}
                value={pipelineStages.map(pipelineStageLabel)}
                onChange={(labels) =>
                  onPipelineStagesChange(
                    labels
                      .map(pipelineStageValue)
                      .filter((v): v is PipelineStageValue => v !== undefined)
                  )
                }
                placeholder="Pipeline Stage"
              />
            </div>
          </>
        )}
      </div>

      <div className="contacts-toolbar__actions">
        {isCoach && (
          <button
            className="contacts-toolbar__send-btn"
            onClick={onBulkSend}
            disabled={selectedCount === 0}
          >
            Send Bulk Outreach{selectedCount > 0 ? ` (${selectedCount})` : ""}
          </button>
        )}
        <button
          className="contacts-toolbar__export-btn"
          onClick={onExportEmails}
          disabled={exporting}
          title="Download the filtered contacts as a CSV email list"
        >
          {exporting ? "Exporting..." : "Export Emails"}
        </button>
        <button className="contacts-toolbar__add-btn" onClick={onAddContact}>
          + Add {typeLabel}
        </button>
      </div>
    </div>
  );
};
